import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { useBenefitPlanFilters } from '@/hooks/useBenefitPlanFilters';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { BENEFIT_PLANS_COLUMNS } from '@/lib/tableColumns';
import { FileText, Plus } from 'lucide-react';
import type { IBenefitPlan } from '@/@types/claims';
import type { TBenefitPlanStatus } from '@/@types/claims';
import type { IInsuranceProvider, IOrgInsuranceType } from '@/@types/insurance';
import { cn } from '@/lib/utils';

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function BenefitPlanStatusBadge({ status }: { status: TBenefitPlanStatus }) {
  return (
    <Badge
      variant={status === 'active' ? 'default' : status === 'draft' ? 'secondary' : 'outline'}
      className={cn(status === 'active' && 'bg-green-600 hover:bg-green-700 text-white')}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function BenefitPlansTableSkeleton({ colCount }: { colCount: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: colCount }).map((_cell, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-28" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function renderBenefitPlanCell(
  colId: string,
  plan: IBenefitPlan,
  providerMap: Map<string, string>,
  typeMap: Map<string, string>
) {
  switch (colId) {
    case 'name':
      return (
        <TableCell key={colId} className="font-medium sticky left-0 z-10 bg-table-container">
          {plan.name}
        </TableCell>
      );
    case 'provider':
      return (
        <TableCell key={colId} className="text-muted-foreground">
          {providerMap.get(plan.providerId) ?? '—'}
        </TableCell>
      );
    case 'type':
      return (
        <TableCell key={colId} className="text-muted-foreground">
          {typeMap.get(plan.insuranceTypeId) ?? '—'}
        </TableCell>
      );
    case 'status':
      return (
        <TableCell key={colId}>
          <BenefitPlanStatusBadge status={plan.status} />
        </TableCell>
      );
    case 'maxAnnualBenefit':
      return (
        <TableCell key={colId} className="text-muted-foreground">
          {formatCurrency(plan.maxAnnualBenefit, plan.currency)}
        </TableCell>
      );
    case 'created':
      return (
        <TableCell key={colId} className="text-muted-foreground">
          {formatDate(plan.createdAt)}
        </TableCell>
      );
    default:
      return <TableCell key={colId} />;
  }
}

export function BenefitPlansListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const rawPlans = useQuery(
    api.insurance.benefitPlans.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const rawProviders = useQuery(
    api.insurance.providers.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const orgTypes = useQuery(
    api.insurance.types.listOrgTypes,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-benefit-plans', BENEFIT_PLANS_COLUMNS);

  const isLoading = orgLoading || rawPlans === undefined;
  const plans: IBenefitPlan[] = rawPlans ?? [];
  const providers: IInsuranceProvider[] = rawProviders ?? [];
  const typeList: IOrgInsuranceType[] = orgTypes ?? [];

  const { filters, filteredPlans, dispatchFilter } = useBenefitPlanFilters(plans);

  const providerMap = new Map<string, string>(providers.map(p => [p._id, p.name]));
  const typeMap = new Map<string, string>(typeList.map(t => [t._id, t.name]));

  const hasActiveFilters =
    filters.search !== '' ||
    filters.status !== 'all' ||
    filters.providerId !== 'all' ||
    filters.insuranceTypeId !== 'all';

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage benefit plans.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Benefit Plans</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${plans.length} total`}
          </p>
        </div>
        <Button onClick={() => navigate('/insurance/benefit-plans/new')}>
          <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
          New Plan
        </Button>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="plan-search" className="sr-only">
            Search benefit plans
          </label>
          <Input
            id="plan-search"
            placeholder="Search by plan name..."
            value={filters.search}
            onChange={e => dispatchFilter({ type: 'SET_SEARCH', query: e.target.value })}
            className="max-w-xs"
          />
          <label htmlFor="plan-status-filter" className="sr-only">
            Filter by status
          </label>
          <Select
            value={filters.status}
            onValueChange={val => dispatchFilter({ type: 'SET_STATUS_FILTER', status: val })}
          >
            <SelectTrigger id="plan-status-filter" className="w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <label htmlFor="plan-provider-filter" className="sr-only">
            Filter by provider
          </label>
          <Select
            value={filters.providerId}
            onValueChange={val => dispatchFilter({ type: 'SET_PROVIDER_FILTER', providerId: val })}
          >
            <SelectTrigger id="plan-provider-filter" className="w-[180px]">
              <SelectValue placeholder="All providers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All providers</SelectItem>
              {providers.map(p => (
                <SelectItem key={p._id} value={p._id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label htmlFor="plan-type-filter" className="sr-only">
            Filter by insurance type
          </label>
          <Select
            value={filters.insuranceTypeId}
            onValueChange={val => dispatchFilter({ type: 'SET_TYPE_FILTER', insuranceTypeId: val })}
          >
            <SelectTrigger id="plan-type-filter" className="w-[180px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {typeList.map(t => (
                <SelectItem key={t._id} value={t._id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow>
                {visibleColumns.map(col => (
                  <TableHead
                    key={col.id}
                    className={cn(col.pinned === 'left' && 'sticky left-0 z-20 bg-table-container')}
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <BenefitPlansTableSkeleton colCount={visibleColumns.length} />}
              {!isLoading &&
                filteredPlans.map(plan => (
                  <TableRow
                    key={plan._id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/insurance/benefit-plans/${plan._id}`)}
                  >
                    {visibleColumns.map(col =>
                      renderBenefitPlanCell(col.id, plan, providerMap, typeMap)
                    )}
                  </TableRow>
                ))}
              {!isLoading && filteredPlans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-1">
                        No benefit plans found
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {hasActiveFilters
                          ? 'Try adjusting your filters or search query'
                          : 'Create your first benefit plan to get started'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
